// File: webelieve-frontend/src/features/notes/components/NoteCommentPanel.tsx
// Slide-out comment panel for note collaboration

import React, { useState, useEffect, useRef } from 'react';
import { useNoteCollaborationStore } from '../stores/useNoteCollaborationStore';

interface NoteCommentPanelProps {
  isOpen: boolean;
  onClose: () => void;
  noteId: string;
}

function formatCommentDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const NoteCommentPanel: React.FC<NoteCommentPanelProps> = ({ isOpen, onClose, noteId }) => {
  const {
    getComments,
    isLoadingComments,
    fetchComments,
    addComment,
    resolveComment,
  } = useNoteCollaborationStore();
  const comments = getComments(noteId);
  const loadingComments = isLoadingComments(noteId);

  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen && noteId) {
      fetchComments(noteId);
    }
  }, [isOpen, noteId, fetchComments]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!newComment.trim() || submitting) return;
    setSubmitting(true);
    await addComment(noteId, newComment.trim());
    setNewComment('');
    setSubmitting(false);
  };

  const handleResolve = async (commentId: string) => {
    await resolveComment(noteId, commentId);
  };

  const unresolvedComments = comments.filter((c) => !c.is_resolved);
  const resolvedComments = comments.filter((c) => c.is_resolved);

  if (!isOpen) return null;

  return (
    <div className="w-72 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col h-full shrink-0">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Comments ({unresolvedComments.length})
        </h3>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Comment list */}
      <div className="flex-1 overflow-y-auto">
        {loadingComments ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-lex-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : unresolvedComments.length === 0 && resolvedComments.length === 0 ? (
          <div className="text-center py-8 px-4">
            <svg className="w-10 h-10 text-gray-200 dark:text-gray-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-xs text-gray-400 dark:text-gray-500">No comments yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {/* Unresolved comments */}
            {unresolvedComments.map((comment) => (
              <div key={comment.id} className="px-4 py-3">
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 rounded-full bg-lex-accent text-lex-accent-contrast text-[10px] font-medium flex items-center justify-center shrink-0 mt-0.5">
                    {(comment.user_name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-xs font-medium text-gray-900 dark:text-white truncate">
                        {comment.user_name || 'Unknown'}
                      </span>
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 shrink-0">
                        {formatCommentDate(comment.created_at)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 whitespace-pre-wrap">
                      {comment.comment_text}
                    </p>
                    <button
                      onClick={() => handleResolve(comment.id)}
                      className="mt-1.5 text-[10px] text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                    >
                      Resolve
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Resolved comments toggle */}
            {resolvedComments.length > 0 && (
              <div className="px-4 py-2">
                <button
                  onClick={() => setShowResolved(!showResolved)}
                  className="text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex items-center gap-1"
                >
                  <svg className={`w-3 h-3 transition-transform ${showResolved ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  {resolvedComments.length} resolved
                </button>
              </div>
            )}

            {/* Resolved comments */}
            {showResolved && resolvedComments.map((comment) => (
              <div key={comment.id} className="px-4 py-3 opacity-60">
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 rounded-full bg-gray-400 text-white text-[10px] font-medium flex items-center justify-center shrink-0 mt-0.5">
                    {(comment.user_name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                        {comment.user_name || 'Unknown'}
                      </span>
                      <svg className="w-3 h-3 text-green-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-through">
                      {comment.comment_text}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New comment input */}
      <div className="px-3 py-3 border-t border-gray-200 dark:border-gray-700">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="Add a comment..."
            rows={2}
            className="flex-1 text-xs px-2.5 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-lex-accent resize-none"
          />
        </div>
        <div className="flex justify-end mt-2">
          <button
            onClick={handleSubmit}
            disabled={!newComment.trim() || submitting}
            className="px-3 py-1 text-xs font-medium bg-lex-accent hover:bg-lex-accent-hover disabled:bg-gray-300 dark:disabled:bg-gray-600 text-lex-accent-contrast rounded transition-colors"
          >
            {submitting ? 'Sending...' : 'Comment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoteCommentPanel;
